import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _classCallCheck from '@babel/runtime/helpers/classCallCheck';
import _createClass from '@babel/runtime/helpers/createClass';
import _possibleConstructorReturn from '@babel/runtime/helpers/possibleConstructorReturn';
import _getPrototypeOf from '@babel/runtime/helpers/getPrototypeOf';
import _inherits from '@babel/runtime/helpers/inherits';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import PropTypes from 'prop-types';
import React from 'react';
import memoize from 'memoize-one';
import { isList, isNotDefined, isFalsy, findDifferenceInList, isObject, isTruthy, isDefined, splitInWhitespace, isInteger, isValidEmail, isValidUrl, getErrorForDateValues, getErrorForTimeValues } from '@togglecorp/fujs';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import produce from 'immer';
import hoistNonReactStatics from 'hoist-non-react-statics';
import _get from '@babel/runtime/helpers/get';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';

var emptyObject = {};
var emptyArray = [];

var hasNoKeys = function hasNoKeys(obj) {
  return isFalsy(obj) || Object.keys(obj).length === 0;
};

var hasNoValues = function hasNoValues(array) {
  return isFalsy(array) || array.length <= 0 || array.every(function (e) {
    return isFalsy(e);
  });
};

var getIdentifierName = function getIdentifierName(element, identifier) {
  var identifierName = identifier(element);
  return isFalsy(identifierName, ['']) ? 'default' : identifierName;
};

var getIdentifierChoice = function getIdentifierChoice(element, identifier, fieldsOrMember) {
  var name = getIdentifierName(element, identifier);
  return fieldsOrMember[name];
};

var accumulateValues = function accumulateValues(obj) {
  var schema = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var settings = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var _settings$noFalsyValu = settings.noFalsyValues,
      noFalsyValues = _settings$noFalsyValu === void 0 ? false : _settings$noFalsyValu,
      _settings$falsyValue = settings.falsyValue,
      falsyValue = _settings$falsyValue === void 0 ? undefined : _settings$falsyValue; // NOTE: if schema is array, the object is the node element

  var member = schema.member,
      fields = schema.fields,
      identifier = schema.identifier,
      keySelector = schema.keySelector;
  var isSchemaForLeaf = isList(schema);
  var isSchemaForArray = !!member && !!keySelector;
  var isSchemaForObject = !!fields;
  var hasIdentifierFunction = !!identifier;

  if (isSchemaForLeaf) {
    if (isNotDefined(obj) && !noFalsyValues) {
      return falsyValue;
    }

    return obj;
  }

  if (isSchemaForArray) {
    var safeObj = obj || emptyArray;
    var values = [];
    safeObj.forEach(function (element) {
      var localMember = hasIdentifierFunction ? getIdentifierChoice(element, identifier, member) : member;
      var value = accumulateValues(element, localMember, settings);
      values.push(value);
    });

    if (hasNoValues(values)) {
      return noFalsyValues ? emptyArray : falsyValue;
    }

    return values;
  }

  if (isSchemaForObject) {
    var _safeObj = obj || emptyObject;

    var _values = {};
    var localFields = hasIdentifierFunction ? getIdentifierChoice(_safeObj, identifier, fields) : fields;
    Object.keys(localFields).forEach(function (fieldName) {
      var value = accumulateValues(_safeObj[fieldName], localFields[fieldName], settings);

      if (value !== undefined) {
        _values[fieldName] = value;
      }
    }); // FIXME: don't copy values if there is nothing to be cleared

    if (hasNoKeys(_values)) {
      return noFalsyValues ? emptyObject : falsyValue;
    }

    return _values;
  }

  console.error('Accumulate Value: Schema is invalid for ', schema);
  return undefined;
};
var accumulateErrors = function accumulateErrors(obj) {
  var schema = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var member = schema.member,
      fields = schema.fields,
      validation = schema.validation,
      keySelector = schema.keySelector,
      identifier = schema.identifier;
  var isSchemaForLeaf = isList(schema);
  var isSchemaForArray = !!member && !!keySelector;
  var hasIdentifierFunction = !!identifier;
  var isSchemaForObject = !!fields;

  if (isSchemaForLeaf) {
    var error;
    schema.every(function (rule) {
      var _rule = rule(obj),
          ok = _rule.ok,
          message = _rule.message;

      if (!ok) {
        error = message;
      }

      return ok;
    });
    return error;
  }

  var errors = {};

  if (validation) {
    var validationErrors = validation(obj);

    if (validationErrors.length > 0) {
      errors.$internal = validationErrors;
    }
  }

  if (isSchemaForArray) {
    var safeObj = obj || emptyArray;
    safeObj.forEach(function (element) {
      var localMember = hasIdentifierFunction ? getIdentifierChoice(element, identifier, member) : member;
      var fieldError = accumulateErrors(element, localMember);

      if (fieldError) {
        var index = keySelector(element);
        errors[index] = fieldError;
      }
    });
    return hasNoKeys(errors) ? undefined : errors;
  }

  if (isSchemaForObject) {
    var _safeObj2 = obj || emptyObject;

    var localFields = hasIdentifierFunction ? getIdentifierChoice(_safeObj2, identifier, fields) : fields;
    Object.keys(localFields).forEach(function (fieldName) {
      var fieldError = accumulateErrors(_safeObj2[fieldName], localFields[fieldName]);

      if (fieldError) {
        errors[fieldName] = fieldError;
      }
    });
    return hasNoKeys(errors) ? undefined : errors;
  }

  console.error('Accumulate Error: Schema is invalid for ', schema);
  return undefined;
};
var accumulateDifferentialErrors = function accumulateDifferentialErrors(oldObj, newObj, oldError) {
  var schema = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};

  if (oldObj === newObj) {
    return oldError;
  } // NOTE: if schema is array, the object is the node element


  var member = schema.member,
      fields = schema.fields,
      validation = schema.validation,
      keySelector = schema.keySelector,
      identifier = schema.identifier;
  var isSchemaForLeaf = isList(schema);
  var isSchemaForArray = !!member && !!keySelector;
  var hasIdentifierFunction = !!identifier;
  var isSchemaForObject = !!fields;

  if (isSchemaForLeaf) {
    var error;
    schema.every(function (rule) {
      var _rule2 = rule(newObj),
          ok = _rule2.ok,
          message = _rule2.message;

      if (!ok) {
        error = message;
      }

      return ok;
    });
    return error;
  }

  var errors = {};

  if (validation) {
    var validationErrors = validation(newObj);

    if (validationErrors.length > 0) {
      errors.$internal = validationErrors;
    }
  }

  if (isSchemaForArray) {
    var safeOldObj = oldObj || emptyArray;
    var safeNewObj = newObj || emptyArray;
    var safeOldError = oldError || emptyObject;

    var _findDifferenceInList = findDifferenceInList(safeOldObj, safeNewObj, keySelector),
        unmodified = _findDifferenceInList.unmodified,
        modified = _findDifferenceInList.modified;
    /*
    // NOTE: no error for newly added elements
    added.forEach((e) => {
        const localMember = hasIdentifierFunction
            ? getIdentifierChoice(element, otherProps)
            : member;
        const fieldError = accumulateErrors(e, localMember);
        if (fieldError) {
            const index = keySelector(e);
            errors[index] = fieldError;
        }
    });
    */


    unmodified.forEach(function (e) {
      var index = keySelector(e);
      errors[index] = safeOldError[index];
    });
    modified.forEach(function (e) {
      var localMember = hasIdentifierFunction ? getIdentifierChoice(e["new"], identifier, member) : member;
      var index = keySelector(e["new"]); // NOTE: clear out errors if different localMember for e.old and e.new

      var forgetOldError = hasIdentifierFunction && getIdentifierName(e["new"], identifier) !== getIdentifierName(e.old, identifier);
      var fieldError = accumulateDifferentialErrors(e.old, e["new"], forgetOldError ? undefined : safeOldError[index], localMember);

      if (fieldError) {
        errors[index] = fieldError;
      }
    });
    return hasNoKeys(errors) ? undefined : errors;
  }

  if (isSchemaForObject) {
    var _safeOldObj = oldObj || emptyObject;

    var _safeNewObj = newObj || emptyObject; // NOTE: clear out errors if different localMember for e.old and e.new


    var forgetOldError = hasIdentifierFunction && getIdentifierName(_safeNewObj, identifier) !== getIdentifierName(_safeOldObj, identifier); // FIXME: forgetOldError can be made a lot better if it only clears
    // error for fields that have validations changed

    var _safeOldError = !forgetOldError && oldError || emptyObject;

    var localFields = hasIdentifierFunction ? getIdentifierChoice(_safeNewObj, identifier, fields) : fields;
    Object.keys(localFields).forEach(function (fieldName) {
      if (_safeOldObj[fieldName] === _safeNewObj[fieldName] && _safeOldError[fieldName]) {
        errors[fieldName] = _safeOldError[fieldName];
        return;
      }

      var fieldError = accumulateDifferentialErrors(_safeOldObj[fieldName], _safeNewObj[fieldName], _safeOldError[fieldName], localFields[fieldName]);

      if (fieldError) {
        errors[fieldName] = fieldError;
      }
    });
    return hasNoKeys(errors) ? undefined : errors;
  }

  console.error('Accumulate Differential Error: Schema is invalid for ', schema);
  return undefined;
};
var analyzeErrors = function analyzeErrors(errors) {
  // handles undefined, null
  if (isFalsy(errors)) {
    return false;
  } // handles empty object {}


  var keys = Object.keys(errors);

  if (keys.length === 0) {
    return false;
  }

  return keys.some(function (key) {
    var subErrors = errors[key]; // handles object

    if (isObject(subErrors)) {
      return analyzeErrors(subErrors);
    } // handles string or array of strings


    return isTruthy(subErrors);
  });
};

var isEqual = function isEqual(a, b) {
  return a === b || Number.isNaN(a) && Number.isNaN(b);
};

var emptyList = [];
var emptyObj = {};

var calculateData = function calculateData(inputValue, schema) {
  var params = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : emptyList;
  var value = inputValue;
  var fields = schema.fields,
      member = schema.member;

  if (fields) {
    var safeObj = value || emptyObj;
    Object.keys(fields).forEach(function (fieldName) {
      var childSchema = fields[fieldName];
      var oldData = safeObj[fieldName];
      var newParams = [].concat(_toConsumableArray(params), [oldData]);
      var newData = typeof childSchema === 'function' ? childSchema.apply(void 0, _toConsumableArray(newParams)) : calculateData(oldData, childSchema, newParams);

      if (!isEqual(oldData, newData)) {
        value = produce(safeObj, function (deferedObj) {
          // eslint-disable-next-line no-param-reassign
          deferedObj[fieldName] = newData;
        });
      }
    });
  } else if (member) {
    var safeList = value || emptyList;
    safeList.forEach(function (oldData, index) {
      var newParams = [].concat(_toConsumableArray(params), [oldData]);
      var newData = calculateData(oldData, member, newParams);

      if (!isEqual(oldData, newData)) {
        value = produce(safeList, function (deferedList) {
          // eslint-disable-next-line no-param-reassign
          deferedList[index] = newData;
        });
      }
    });
  }

  return value;
};

var computeOutputs = function computeOutputs(initialValue, schema) {
  var maxIteration = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 100;
  var value = initialValue;

  if (!schema) {
    return value;
  }

  var i;

  for (i = 0; i < maxIteration; i += 1) {
    var newValue = calculateData(value, schema, [value]);

    if (newValue === value) {
      break;
    }

    value = newValue;
  }

  if (i >= maxIteration) {
    console.warn("Faram computed for ".concat(i, " times."));
  }

  return value;
};

/*
 * FormContext
 *
 * A react context to pass apis
 */

var FormContext = React.createContext(undefined);

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(source, true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(source).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var propTypes = {
  faramInboundTransform: PropTypes.func,
  faramOutboundTransform: PropTypes.func
};
var defaultProps = {
  faramInboundTransform: undefined,
  faramOutboundTransform: undefined
};
var FormElement = (function (elementType) {
  return function (WrappedComponent) {
    var FormElement =
    /*#__PURE__*/
    function (_React$Component) {
      _inherits(FormElement, _React$Component);

      function FormElement() {
        var _getPrototypeOf2;

        var _this;

        _classCallCheck(this, FormElement);

        for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
          args[_key] = arguments[_key];
        }

        _this = _possibleConstructorReturn(this, (_getPrototypeOf2 = _getPrototypeOf(FormElement)).call.apply(_getPrototypeOf2, [this].concat(args)));

        _this.calculateValue = function (value) {
          var faramInboundTransform = _this.props.faramInboundTransform;

          if (!faramInboundTransform) {
            return value;
          }

          return faramInboundTransform(value);
        };

        _this.calculateOnChange = memoize(function (onChange) {
          return onChange && function (value, info) {
            var _this$props = _this.props,
                faramOutboundTransform = _this$props.faramOutboundTransform,
                oldValue = _this$props.value;

            if (!faramOutboundTransform) {
              onChange(value, info);
              return;
            }

            onChange(faramOutboundTransform(value, oldValue), info);
          };
        });

        _this.renderWrappedComponent = function () {
          var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
              api = _ref.api;

          var newProps = FormElement.calculateProps(api, _this.props);

          if ('onChange' in newProps) {
            newProps.onChange = _this.calculateOnChange(newProps.onChange);
          }

          if ('value' in newProps) {
            newProps.value = _this.calculateValue(newProps.value);
          }

          return React.createElement(WrappedComponent, newProps);
        };

        return _this;
      }

      _createClass(FormElement, [{
        key: "render",
        value: function render() {
          return React.createElement(FormContext.Consumer, null, this.renderWrappedComponent);
        }
      }]);

      return FormElement;
    }(React.Component);

    FormElement.propTypes = propTypes;
    FormElement.defaultProps = defaultProps;

    FormElement.calculateProps = function (api, originalProps) {
      var faramInboundTransform = originalProps.faramInboundTransform,
          faramOutboundTransform = originalProps.faramOutboundTransform,
          props = _objectWithoutProperties(originalProps, ["faramInboundTransform", "faramOutboundTransform"]);

      if (!api) {
        return props;
      }

      var _api$getCalculatedPro = api.getCalculatedProps(elementType, props),
          injectedProps = _api$getCalculatedPro.injectedProps,
          otherProps = _api$getCalculatedPro.otherProps;

      if (!injectedProps) {
        return props;
      } // NOTE: user can still override the calculated props, if need be


      return _objectSpread({}, injectedProps, {}, otherProps);
    };

    return hoistNonReactStatics(FormElement, WrappedComponent);
  };
});

var FaramInputElement = FormElement('input');
var FaramOutputElement = FormElement('output');
var FaramErrorMessageElement = FormElement('errorMessage');
var FaramErrorIndicatorElement = FormElement('errorIndicator');
var FaramActionElement = FormElement('action');
var FaramListElement = FormElement('list');
var FaramSortableListElement = FormElement('sortableList');

function ownKeys$1(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$1(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$1(source, true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$1(source).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

var FormElementApi =
/*#__PURE__*/
function () {
  function FormElementApi() {
    var _this = this;

    _classCallCheck(this, FormElementApi);

    this.setProps = function (props) {
      _this.props = _objectSpread$1({}, props);
    };

    this.getCalculatedProps = function (elementType, props) {
      var _ref = _this.handlers[elementType] || {},
          getPropsFromApi = _ref.getPropsFromApi,
          calculateElementProps = _ref.calculateElementProps;

      if (!getPropsFromApi || !calculateElementProps) {
        // console.error(`FormElement: Handler not registered for: ${elementType}`);
        return {
          otherProps: props
        };
      }

      var _getPropsFromApi = getPropsFromApi(props),
          apiProps = _getPropsFromApi.apiProps,
          otherProps = _getPropsFromApi.otherProps; // apiProps should be undefined when apiProps cannot be calculated
      // by getPropsFromApi method, but it still strips known keys from input props


      if (!apiProps) {
        return {
          otherProps: otherProps
        };
      }

      var injectedProps = calculateElementProps(apiProps);
      return {
        injectedProps: injectedProps,
        otherProps: otherProps
      };
    };
  }

  _createClass(FormElementApi, [{
    key: "getHandler",
    // eslint-disable-next-line class-methods-use-this
    value: function getHandler() {
      return {};
    }
  }]);

  return FormElementApi;
}();

function ownKeys$2(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$2(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$2(source, true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$2(source).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var emptyObject$1 = {};

var FaramGroupApi =
/*#__PURE__*/
function (_FormElementApi) {
  _inherits(FaramGroupApi, _FormElementApi);

  function FaramGroupApi() {
    var _this;

    _classCallCheck(this, FaramGroupApi);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(FaramGroupApi).call(this));
    _this.onChangeMemory = {};
    _this.onClickMemory = {};

    _this.getEmptyElement = function () {
      return emptyObject$1;
    };

    _this.setProps = function (props) {
      _this.props = _objectSpread$2({}, props, {
        onChange: function onChange(value, info) {
          // NOTE: Save these values in this.props so that above
          // destructuring keeps working before setProps is
          // again called.
          _this.props.value = value;
          props.onChange(value, info);
        }
      });
    };

    _this.getValue = function (faramElementName) {
      return _this.props.value ? _this.props.value[faramElementName] : undefined;
    };

    _this.getError = function (faramElementName) {
      return _this.props.error ? _this.props.error[faramElementName] : undefined;
    };

    _this.getInternalError = function () {
      return _this.props.error ? _this.props.error.$internal : undefined;
    };

    _this.getChangeDelay = function () {
      return _this.props.changeDelay;
    };

    _this.isReadOnly = function () {
      return _this.props.readOnly;
    };

    _this.isDisabled = function () {
      return _this.props.disabled;
    };

    _this.getNewValue = function (key, oldValue, newValue) {
      return _objectSpread$2({}, oldValue, _defineProperty({}, key, newValue));
    };

    _this.getNewInfo = function (key, value, info, infoFromProps) {
      var faramElementName = info && info.faramElementName || [];
      var firstRun = faramElementName.length === 0;

      if (firstRun) {
        return _objectSpread$2({}, info || infoFromProps, {
          faramElementName: [key],
          faramElementValue: value
        });
      }

      return _objectSpread$2({}, info, {
        faramElementName: [key].concat(_toConsumableArray(info.faramElementName))
      });
    };

    _this.getOnChange = function (faramElementName, faramInfo) {
      if (_this.onChangeMemory[faramElementName]) {
        return _this.onChangeMemory[faramElementName];
      }

      var newOnChange = function newOnChange(value, info) {
        if (!_this.props.onChange) {
          return;
        }

        var newValue = _this.getNewValue(faramElementName, _this.props.value, value);

        var newInfo = _this.getNewInfo(faramElementName, value, info, faramInfo);

        _this.props.onChange(newValue, newInfo);
      };

      _this.onChangeMemory[faramElementName] = newOnChange;
      return newOnChange;
    };

    _this.getOnClick = function (faramElementName, faramAction) {
      if (_this.onClickMemory[faramElementName]) {
        return _this.onClickMemory[faramElementName];
      }

      var newOnClick = function newOnClick(clickParams) {
        var newValue = faramAction(_this.props.value || _this.getEmptyElement(), faramElementName, clickParams); // Button doesn't have children, so no need to propagate faramInfo

        _this.props.onChange(newValue);
      };

      _this.onClickMemory[faramElementName] = newOnClick;
      return newOnClick;
    };

    _this.handlers = _this.getHandler();
    return _this;
  }

  _createClass(FaramGroupApi, [{
    key: "getHandler",
    value: function getHandler() {
      var _this2 = this;

      return _objectSpread$2({}, _get(_getPrototypeOf(FaramGroupApi.prototype), "getHandler", this).call(this), {
        input: {
          getPropsFromApi: function getPropsFromApi(_ref) {
            var faramElementName = _ref.faramElementName,
                faramInfo = _ref.faramInfo,
                otherProps = _objectWithoutProperties(_ref, ["faramElementName", "faramInfo"]);

            return {
              apiProps: !isFalsy(faramElementName, ['']) ? {
                faramElementName: faramElementName,
                faramInfo: faramInfo
              } : undefined,
              otherProps: otherProps
            };
          },
          calculateElementProps: function calculateElementProps(_ref2) {
            var faramElementName = _ref2.faramElementName,
                faramInfo = _ref2.faramInfo;
            return {
              value: _this2.getValue(faramElementName),
              error: _this2.getError(faramElementName),
              onChange: _this2.getOnChange(faramElementName, faramInfo),
              disabled: _this2.isDisabled(),
              readOnly: _this2.isReadOnly(),
              changeDelay: _this2.getChangeDelay()
            };
          }
        },
        output: {
          getPropsFromApi: function getPropsFromApi(_ref3) {
            var faramElementName = _ref3.faramElementName,
                otherProps = _objectWithoutProperties(_ref3, ["faramElementName"]);

            return {
              apiProps: !isFalsy(faramElementName, ['']) ? {
                faramElementName: faramElementName
              } : undefined,
              otherProps: otherProps
            };
          },
          calculateElementProps: function calculateElementProps(_ref4) {
            var faramElementName = _ref4.faramElementName;
            return {
              value: _this2.getValue(faramElementName)
            };
          }
        },
        errorMessage: {
          getPropsFromApi: function getPropsFromApi(_ref5) {
            var faramElement = _ref5.faramElement,
                otherProps = _objectWithoutProperties(_ref5, ["faramElement"]);

            return {
              apiProps: isTruthy(faramElement) ? {} : undefined,
              otherProps: otherProps
            };
          },
          calculateElementProps: function calculateElementProps() {
            return {
              errors: _this2.getInternalError()
            };
          }
        },
        errorIndicator: {
          getPropsFromApi: function getPropsFromApi(_ref6) {
            var faramElementName = _ref6.faramElementName,
                otherProps = _objectWithoutProperties(_ref6, ["faramElementName"]);

            return {
              apiProps: !isFalsy(faramElementName, ['']) ? {
                faramElementName: faramElementName
              } : undefined,
              otherProps: otherProps
            };
          },
          calculateElementProps: function calculateElementProps(_ref7) {
            var faramElementName = _ref7.faramElementName;

            var errors = _this2.getError(faramElementName);

            var calculatedProps = {
              hasError: analyzeErrors(errors),
              errors: errors
            };
            return calculatedProps;
          }
        },
        action: {
          getPropsFromApi: function getPropsFromApi(_ref8) {
            var faramElementName = _ref8.faramElementName,
                faramAction = _ref8.faramAction,
                otherProps = _objectWithoutProperties(_ref8, ["faramElementName", "faramAction"]);

            return {
              apiProps: !isFalsy(faramElementName, ['']) && faramAction ? {
                faramElementName: faramElementName,
                faramAction: faramAction
              } : undefined,
              otherProps: otherProps
            };
          },
          calculateElementProps: function calculateElementProps(_ref9) {
            var faramElementName = _ref9.faramElementName,
                faramAction = _ref9.faramAction;
            return {
              disabled: _this2.isDisabled() || _this2.isReadOnly(),
              changeDelay: _this2.getChangeDelay(),
              onClick: _this2.getOnClick(faramElementName, faramAction)
            };
          }
        }
      });
    } // NOTE: This function is overridden by FaramListApi

  }]);

  return FaramGroupApi;
}(FormElementApi);

var propTypes$1 = {
  children: PropTypes.node.isRequired,
  onChange: PropTypes.func,
  value: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types
  error: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  changeDelay: PropTypes.number
};
var defaultProps$1 = {
  onChange: undefined,
  value: {},
  error: {},
  disabled: false,
  readOnly: false,
  changeDelay: undefined
};

var FaramGroup =
/*#__PURE__*/
function (_React$PureComponent) {
  _inherits(FaramGroup, _React$PureComponent);

  function FaramGroup() {
    var _getPrototypeOf2;

    var _this;

    _classCallCheck(this, FaramGroup);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _possibleConstructorReturn(this, (_getPrototypeOf2 = _getPrototypeOf(FaramGroup)).call.apply(_getPrototypeOf2, [this].concat(args)));
    _this.api = new FaramGroupApi();
    return _this;
  }

  _createClass(FaramGroup, [{
    key: "render",
    value: function render() {
      var _this$props = this.props,
          children = _this$props.children,
          value = _this$props.value,
          error = _this$props.error,
          disabled = _this$props.disabled,
          onChange = _this$props.onChange,
          readOnly = _this$props.readOnly,
          changeDelay = _this$props.changeDelay;
      this.api.setProps({
        value: value,
        error: error,
        disabled: disabled,
        onChange: onChange,
        readOnly: readOnly,
        changeDelay: changeDelay
      }); // Context Provider is pure so we need to pass new object
      // as value everytime.

      var providerValue = {
        api: this.api
      };
      return React.createElement(FormContext.Provider, {
        value: providerValue
      }, children);
    }
  }]);

  return FaramGroup;
}(React.PureComponent);

FaramGroup.propTypes = propTypes$1;
FaramGroup.defaultProps = defaultProps$1;
var FaramGroup$1 = FaramInputElement(FaramGroup);

function ownKeys$3(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$3(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$3(source, true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$3(source).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

var noOp = function noOp() {};

var propTypes$2 = {
  /* class name for styling */
  className: PropTypes.string,

  /* children of the form */
  children: PropTypes.node.isRequired,
  // eslint-disable-line

  /* schema for validation */
  schema: PropTypes.object.isRequired,
  // eslint-disable-line react/forbid-prop-types

  /* schema for calculation */
  computeSchema: PropTypes.objectOf(PropTypes.any),

  /* fn to be called when value of any element change */
  onChange: PropTypes.func,

  /* fn to be called when form validation fails */
  onValidationFailure: PropTypes.func,

  /* fn to be called when form validation succeds */
  onValidationSuccess: PropTypes.func,

  /* object with values */
  value: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types

  /* Error for every field of form */
  error: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types

  /* Disable all elements in form */
  disabled: PropTypes.bool,

  /* Set elements to readonly in form */
  readOnly: PropTypes.bool,

  /* Delay every input component in form */
  changeDelay: PropTypes.number,

  /* Submit method setter to use externally */
  setSubmitFunction: PropTypes.func
};
var defaultProps$2 = {
  className: '',
  onChange: noOp,
  onValidationFailure: noOp,
  onValidationSuccess: noOp,
  disabled: false,
  readOnly: false,
  changeDelay: 100,
  // ms
  computeSchema: {},
  value: {},
  error: {},
  setSubmitFunction: undefined
};
var emptyObject$2 = {}; // TODO: memoizing accumulateErrors, analyzeErrors, accumulateValues
// should be done later

var handleSubmit = function handleSubmit(_ref) {
  var value = _ref.value,
      schema = _ref.schema,
      onValidationFailure = _ref.onValidationFailure,
      onValidationSuccess = _ref.onValidationSuccess;
  var errors = accumulateErrors(value, schema);
  var hasErrors = analyzeErrors(errors);

  if (hasErrors) {
    onValidationFailure(errors);
    return value;
  }

  var values = accumulateValues(value, schema, {
    noFalsyValues: true
  });
  var valuesWithNull = accumulateValues(value, schema, {
    noFalsyValues: false,
    falsyValue: null
  });
  onValidationSuccess(valuesWithNull, values);
  return values;
}; // NOTE: detachedFaram only calls handle submit and doesn't trigger
// saving of new values or errors


var detachedFaram = handleSubmit;
var memoizedComputeOutputs = memoize(computeOutputs);

var handleChange = function handleChange(_ref2) {
  var value = _ref2.value,
      info = _ref2.info,
      onChange = _ref2.onChange,
      computeSchema = _ref2.computeSchema,
      schema = _ref2.schema,
      oldValue = _ref2.oldValue,
      oldError = _ref2.oldError,
      force = _ref2.force;
  var newValue = memoizedComputeOutputs(value, computeSchema);

  if (!force && value === newValue) {
    return;
  }

  var newError = accumulateDifferentialErrors(oldValue, newValue, oldError, schema);

  var newInfo = _objectSpread$3({}, info, {
    hasError: analyzeErrors(newError)
  });

  onChange(newValue || emptyObject$2, newError || emptyObject$2, newInfo);
};
/*
 * Form Component for field validations and values aggregation
 */


var Faram =
/*#__PURE__*/
function (_React$PureComponent) {
  _inherits(Faram, _React$PureComponent);

  function Faram() {
    var _getPrototypeOf2;

    var _this;

    _classCallCheck(this, Faram);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _possibleConstructorReturn(this, (_getPrototypeOf2 = _getPrototypeOf(Faram)).call.apply(_getPrototypeOf2, [this].concat(args)));

    _this.submit = function () {
      clearTimeout(_this.changeTimeout);
      var _this$props = _this.props,
          disabled = _this$props.disabled,
          changeDelay = _this$props.changeDelay;

      if (disabled) {
        return;
      } // Add some delay to submit


      _this.changeTimeout = setTimeout(function () {
        var _this$props2 = _this.props,
            value = _this$props2.value,
            schema = _this$props2.schema,
            onValidationFailure = _this$props2.onValidationFailure,
            onValidationSuccess = _this$props2.onValidationSuccess;
        handleSubmit({
          value: value,
          schema: schema,
          onValidationFailure: onValidationFailure,
          onValidationSuccess: onValidationSuccess
        });
      }, changeDelay);
    };

    _this.handleSubmitClick = function (e) {
      e.preventDefault();
      e.stopPropagation();

      _this.submit(); // NOTE: Returning false will not submit & redirect


      return false;
    };

    _this.handleFormChange = function (value, info) {
      var _this$props3 = _this.props,
          onChange = _this$props3.onChange,
          computeSchema = _this$props3.computeSchema,
          schema = _this$props3.schema,
          oldValue = _this$props3.value,
          oldError = _this$props3.error;
      handleChange({
        value: value,
        info: info,
        onChange: onChange,
        computeSchema: computeSchema,
        schema: schema,
        oldValue: oldValue,
        oldError: oldError,
        force: true
      });
    };

    return _this;
  }

  _createClass(Faram, [{
    key: "componentWillMount",
    value: function componentWillMount() {
      var setSubmitFunction = this.props.setSubmitFunction;

      if (setSubmitFunction) {
        setSubmitFunction(this.submit);
      }

      var _this$props4 = this.props,
          onChange = _this$props4.onChange,
          computeSchema = _this$props4.computeSchema,
          schema = _this$props4.schema,
          value = _this$props4.value,
          error = _this$props4.error;
      handleChange({
        value: value,
        info: {
          isComputed: true
        },
        onChange: onChange,
        computeSchema: computeSchema,
        schema: schema,
        oldValue: value,
        oldError: error
      });
    }
  }, {
    key: "componentWillReceiveProps",
    value: function componentWillReceiveProps(nextProps) {
      var newValue = nextProps.value,
          newComputeSchema = nextProps.computeSchema,
          newSchema = nextProps.schema,
          onChange = nextProps.onChange;
      var _this$props5 = this.props,
          oldValue = _this$props5.value,
          oldError = _this$props5.error;
      handleChange({
        value: newValue,
        info: {
          isComputed: true
        },
        onChange: onChange,
        computeSchema: newComputeSchema,
        schema: newSchema,
        oldValue: oldValue,
        oldError: oldError
      });
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      clearTimeout(this.changeTimeout);
      var setSubmitFunction = this.props.setSubmitFunction;

      if (setSubmitFunction) {
        setSubmitFunction(undefined);
      }
    } // NOTE:
    // Submit method that can be called externally
    // Use detachedFaram instead of calling submit externally

  }, {
    key: "render",
    value: function render() {
      var _this$props6 = this.props,
          children = _this$props6.children,
          className = _this$props6.className,
          value = _this$props6.value,
          error = _this$props6.error,
          disabled = _this$props6.disabled,
          readOnly = _this$props6.readOnly,
          changeDelay = _this$props6.changeDelay,
          schema = _this$props6.schema,
          computeSchema = _this$props6.computeSchema,
          onChange = _this$props6.onChange,
          onValidationFailure = _this$props6.onValidationFailure,
          onValidationSuccess = _this$props6.onValidationSuccess,
          setSubmitFunction = _this$props6.setSubmitFunction,
          otherProps = _objectWithoutProperties(_this$props6, ["children", "className", "value", "error", "disabled", "readOnly", "changeDelay", "schema", "computeSchema", "onChange", "onValidationFailure", "onValidationSuccess", "setSubmitFunction"]);

      return React.createElement("form", {
        className: className,
        onSubmit: this.handleSubmitClick,
        noValidate: true
      }, React.createElement(FaramGroup$1, _extends({
        onChange: this.handleFormChange,
        value: value,
        error: error,
        disabled: disabled,
        readOnly: readOnly,
        changeDelay: changeDelay
      }, otherProps), children));
    }
  }]);

  return Faram;
}(React.PureComponent);

Faram.propTypes = propTypes$2;
Faram.defaultProps = defaultProps$2;

var exclusiveInBetweenCondition = function exclusiveInBetweenCondition(min, max) {
  return function (value) {
    var ok = isFalsy(value) || isFalsy(min) && isFalsy(max) || isFalsy(min) && value < max || isFalsy(max) && value > min || value < max && value > min;
    var message = isFalsy(min) && isFalsy(max) && '' || isFalsy(min) && "Value must be less than ".concat(max) || isFalsy(max) && "Value must be greater than ".concat(min) || "Value must be exclusively in between ".concat(min, " and ").concat(max);
    return {
      ok: ok,
      message: message
    };
  };
};
var inclusiveInBetweenCondition = function inclusiveInBetweenCondition(min, max) {
  return function (value) {
    var ok = isFalsy(value) || isFalsy(min) && isFalsy(max) || isFalsy(min) && value <= max || isFalsy(max) && value >= min || value <= max && value >= min;
    var message = isFalsy(min) && isFalsy(max) && '' || isFalsy(min) && "Value must be less than or equal to ".concat(max) || isFalsy(max) && "Value must be greater than or equal to ".concat(min) || "Value must be in between ".concat(min, " and ").concat(max);
    return {
      ok: ok,
      message: message
    };
  };
};
var lessThanCondition = function lessThanCondition(n) {
  return function (value) {
    var ok = isFalsy(value) || value < n;
    return {
      ok: ok,
      message: "Value must be less than ".concat(n)
    };
  };
};
var greaterThanCondition = function greaterThanCondition(n) {
  return function (value) {
    var ok = isFalsy(value) || value > n;
    return {
      ok: ok,
      message: "Value must be greater than ".concat(n)
    };
  };
};
var lessThanOrEqualToCondition = function lessThanOrEqualToCondition(n) {
  return function (value) {
    var ok = isFalsy(value) || value <= n;
    return {
      ok: ok,
      message: "Value must be less than or equal to ".concat(n)
    };
  };
};
var greaterThanOrEqualToCondition = function greaterThanOrEqualToCondition(n) {
  return function (value) {
    var ok = isFalsy(value) || value >= n;
    return {
      ok: ok,
      message: "Value must be greater than or equal to ".concat(n)
    };
  };
};
var lengthLessThanCondition = function lengthLessThanCondition(n) {
  return function (value) {
    var ok = isFalsy(value) || value.length < n;
    return {
      ok: ok,
      message: "Length must be less than ".concat(n)
    };
  };
};
var lengthGreaterThanCondition = function lengthGreaterThanCondition(n) {
  return function (value) {
    var ok = isFalsy(value) || value.length > n;
    return {
      ok: ok,
      message: "Length must be greater than ".concat(n)
    };
  };
};
var lengthEqualToCondition = function lengthEqualToCondition(n) {
  return function (value) {
    var ok = isFalsy(value) || value.length === n;
    return {
      ok: ok,
      message: "Length must be exactly ".concat(n)
    };
  };
};
var requiredCondition = function requiredCondition(value) {
  var ok = isDefined(value) && !(typeof value === 'string' && splitInWhitespace(value).length <= 0 || Array.isArray(value) && value.length <= 0);
  return {
    ok: ok,
    message: 'Field must not be empty'
  };
};
var numberCondition = function numberCondition(value) {
  var ok = !Number.isNaN(value) && (isFalsy(value) || !isFalsy(+value));
  return {
    ok: ok,
    message: 'Value must be a number'
  };
};
var integerCondition = function integerCondition(value) {
  var ok = !Number.isNaN(value) && (isFalsy(value) || isInteger(+value));
  return {
    ok: ok,
    message: 'Value must be a integer'
  };
};
var emailCondition = function emailCondition(value) {
  var ok = isFalsy(value, ['']) || isValidEmail(value);
  return {
    ok: ok,
    message: 'Value must be a valid email'
  };
};
var urlCondition = function urlCondition(value) {
  var ok = isFalsy(value, ['']) || isValidUrl(value);
  return {
    ok: ok,
    message: 'Value must be a valid URL'
  };
};
var lenientUrlCondition = function lenientUrlCondition(value) {
  var ok = isFalsy(value, ['']) || isValidUrl(value.startsWith('http://') || value.startsWith('https://') ? value : "http://".concat(value));
  return {
    ok: ok,
    message: 'Value must be a valid URL'
  };
};
var dateCondition = function dateCondition(value) {
  var error;

  if (!isFalsy(value, '')) {
    var dates = value.split('-');

    var _dates = _slicedToArray(dates, 3),
        yearValue = _dates[0],
        monthValue = _dates[1],
        dayValue = _dates[2];

    error = getErrorForDateValues({
      yearValue: yearValue,
      monthValue: monthValue,
      dayValue: dayValue
    });
  }

  return {
    ok: !error,
    message: error
  };
};
var TIME_SEPARATOR = ':';
var timeCondition = function timeCondition(value) {
  var error;

  if (!isFalsy(value, '')) {
    var values = value.split(TIME_SEPARATOR);

    var _values = _slicedToArray(values, 3),
        hourValue = _values[0],
        minuteValue = _values[1],
        secondValue = _values[2];

    error = getErrorForTimeValues({
      hourValue: hourValue,
      minuteValue: minuteValue,
      secondValue: secondValue
    });
  }

  return {
    ok: !error,
    message: error
  };
};

function ownKeys$4(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$4(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$4(source, true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$4(source).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var emptyArray$1 = [];

var FaramListApi =
/*#__PURE__*/
function (_FaramGroupApi) {
  _inherits(FaramListApi, _FaramGroupApi);

  function FaramListApi(props) {
    var _this;

    _classCallCheck(this, FaramListApi);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(FaramListApi).call(this, props));

    _this.getEmptyElement = function () {
      return emptyArray$1;
    };

    _this.getNewValue = function (key, oldValue, newValue) {
      var result = _toConsumableArray(oldValue);

      result[key] = newValue;
      return result;
    };

    _this.getError = function (faramElementName) {
      var val = _this.getValue(faramElementName);

      var index = isDefined(val) ? _this.props.keySelector(val) : undefined;
      return _this.props.error ? _this.props.error[index] : undefined;
    };

    _this.handlers = _this.getHandler();
    return _this;
  }

  _createClass(FaramListApi, [{
    key: "getHandler",
    value: function getHandler() {
      var _this2 = this;

      return _objectSpread$4({}, _get(_getPrototypeOf(FaramListApi.prototype), "getHandler", this).call(this), {
        list: {
          getPropsFromApi: function getPropsFromApi(_ref) {
            var faramElement = _ref.faramElement,
                otherProps = _objectWithoutProperties(_ref, ["faramElement"]);

            return {
              apiProps: faramElement ? {} : undefined,
              otherProps: otherProps
            };
          },
          calculateElementProps: function calculateElementProps() {
            return {
              data: _this2.props.value,
              keySelector: _this2.props.keySelector
            };
          }
        },
        sortableList: {
          getPropsFromApi: function getPropsFromApi(_ref2) {
            var faramElement = _ref2.faramElement,
                otherProps = _objectWithoutProperties(_ref2, ["faramElement"]);

            return {
              apiProps: faramElement ? {} : undefined,
              otherProps: otherProps
            };
          },
          calculateElementProps: function calculateElementProps() {
            return {
              data: _this2.props.value,
              onChange: _this2.props.onChange,
              keySelector: _this2.props.keySelector
            };
          }
        }
      });
    }
  }]);

  return FaramListApi;
}(FaramGroupApi);

var propTypes$3 = {
  children: PropTypes.node,
  onChange: PropTypes.func,
  value: PropTypes.array,
  // eslint-disable-line react/forbid-prop-types
  error: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types
  disabled: PropTypes.bool,
  keySelector: PropTypes.func,
  readOnly: PropTypes.bool,
  changeDelay: PropTypes.number
};
var defaultProps$3 = {
  onChange: undefined,
  value: [],
  error: {},
  disabled: false,
  readOnly: false,
  changeDelay: undefined,
  children: null,
  keySelector: function keySelector(d) {
    return d.key;
  }
};

var FaramList =
/*#__PURE__*/
function (_React$PureComponent) {
  _inherits(FaramList, _React$PureComponent);

  function FaramList() {
    var _getPrototypeOf2;

    var _this;

    _classCallCheck(this, FaramList);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _possibleConstructorReturn(this, (_getPrototypeOf2 = _getPrototypeOf(FaramList)).call.apply(_getPrototypeOf2, [this].concat(args)));
    _this.api = new FaramListApi();
    return _this;
  }

  _createClass(FaramList, [{
    key: "render",
    value: function render() {
      var children = this.props.children;
      var _this$props = this.props,
          value = _this$props.value,
          error = _this$props.error,
          disabled = _this$props.disabled,
          onChange = _this$props.onChange,
          readOnly = _this$props.readOnly,
          changeDelay = _this$props.changeDelay,
          keySelector = _this$props.keySelector;
      this.api.setProps({
        value: value,
        error: error,
        disabled: disabled,
        onChange: onChange,
        readOnly: readOnly,
        changeDelay: changeDelay,
        keySelector: keySelector
      }); // Context Provider is pure so we need to pass new object
      // as value everytime.

      var providerValue = {
        api: this.api
      };
      return React.createElement(FormContext.Provider, {
        value: providerValue
      }, children);
    }
  }]);

  return FaramList;
}(React.PureComponent);

FaramList.propTypes = propTypes$3;
FaramList.defaultProps = defaultProps$3;
var index = FaramInputElement(FaramList);

export default Faram;
export { FaramActionElement, FaramErrorIndicatorElement, FaramErrorMessageElement, FaramGroup$1 as FaramGroup, FaramInputElement, index as FaramList, FaramListElement, FaramOutputElement, FaramSortableListElement, FormElement, accumulateDifferentialErrors, accumulateErrors, accumulateValues, analyzeErrors, dateCondition, detachedFaram, emailCondition, exclusiveInBetweenCondition, greaterThanCondition, greaterThanOrEqualToCondition, inclusiveInBetweenCondition, integerCondition, lengthEqualToCondition, lengthGreaterThanCondition, lengthLessThanCondition, lenientUrlCondition, lessThanCondition, lessThanOrEqualToCondition, numberCondition, requiredCondition, timeCondition, urlCondition };
